// The socket contract, used by both ends. Every inbound payload is parsed with these schemas before
// it reaches game logic.
import { z } from "zod";
export const GAME_CODE_RE = /^[A-Z0-9]{4,8}$/;
const gameCode = z.string().trim().toUpperCase().regex(GAME_CODE_RE, "That doesn't look like a game code.");
const seatId = z.string().min(1).max(64);
const characterId = z.string().min(1).max(64);
const discordId = z.string().min(1).max(64);
export const gameCreateSchema = z.object({
    /** The onboarded Discord server the game belongs to; only its members can join. */
    guildId: discordId,
    /** Voice channel used as the town square — chosen from the guild's channels by storyteller on game create. */
    mainChannelId: z.string().max(64).nullable().optional(),
    /** Category the bot creates each seated player's private House channel under. */
    housesCategoryId: z.string().max(64).nullable().optional(),
});
export const gameJoinSchema = z.object({
    code: gameCode,
    /**
     * Omitted means "stay where I was": a refresh must never change which side of the table you're
     * on.
     */
    asStoryteller: z.boolean().optional(),
});
export const gameLeaveSchema = z.object({});
/** A player giving up their seat from the home screen, freeing them to join another game. */
export const gameResignSchema = z.object({ code: gameCode });
export const stImportScriptSchema = z.object({
    /** Raw text of a standard script JSON file; parsed server-side so both ends can't disagree. */
    json: z.string().min(2).max(1_000_000),
});
export const stRandomizeSchema = z.object({});
export const stAssignRoleSchema = z.object({
    seatId,
    characterId: characterId.nullable(),
});
export const stRevealRolesSchema = z.object({});
export const stSetSeatStatusSchema = z.object({
    seatId,
    poisoned: z.boolean().optional(),
    drunk: z.boolean().optional(),
    protected: z.boolean().optional(),
    /** Overrides good/evil independent of character; null clears back to "use their character". */
    alignmentOverride: z.enum(["good", "evil"]).nullable().optional(),
    alive: z.boolean().optional(),
    /** Only meaningful alongside alive:false — "executed" vs a night kill. */
    deathCause: z.enum(["executed", "night", "other"]).optional(),
    /** Reveal this seat's character to the whole table (or take the reveal back). */
    revealCharacter: z.boolean().optional(),
    isTraveler: z.boolean().optional(),
    ghostVoteUsed: z.boolean().optional(),
});
export const stMoveSeatSchema = z.object({
    seatId,
    /** -1 swaps with the seat before it in the circle, +1 with the seat after. */
    direction: z.union([z.literal(-1), z.literal(1)]),
});
export const stRandomizeSeatingSchema = z.object({});
export const stRemoveSeatSchema = z.object({ seatId });
export const stStartNightSchema = z.object({});
export const stEndNightSchema = z.object({});
export const stNightEntrySchema = z.object({
    entryId: z.string().min(1).max(128),
    action: z.enum(["confirm", "skip", "reset"]),
});
export const stNightCursorSchema = z.object({ cursor: z.number().int().min(0).max(500) });
export const stStartDaySchema = z.object({
    /** Minutes of discussion. The bot pulls everyone back when it expires. */
    minutes: z.number().min(0.25).max(120),
    muteOnReturn: z.boolean().default(false),
});
export const stStartCountdownSchema = z.object({
    minutes: z.number().min(0.25).max(120),
    label: z.string().max(60).default("Countdown"),
});
export const stClearCountdownSchema = z.object({});
export const stUnmuteAllSchema = z.object({});
export const stOpenNominationsSchema = z.object({});
export const stStartVoteSchema = z.object({
    nominationId: z.string().min(1).max(64),
    /** How long the clock hand spends on each seat. */
    secondsPerSeat: z.number().min(0.5).max(30),
});
export const stCloseNominationSchema = z.object({ nominationId: z.string().min(1).max(64) });
export const stClearNominationsSchema = z.object({});
export const stExecuteSchema = z.object({ nominationId: z.string().min(1).max(64) });
export const stExileSchema = z.object({ nominationId: z.string().min(1).max(64) });
export const stEndGameSchema = z.object({
    winningTeam: z.enum(["good", "evil"]),
    notes: z.string().max(4000).default(""),
});
/** Ending a game from the home screen: with a result it is archived as usual, without one it is
 *  deleted and leaves no history. */
export const gameEndSchema = z.object({
    code: gameCode,
    result: stEndGameSchema.nullable(),
});
export const stViewNotepadSchema = z.object({ seatId: seatId.nullable() });
export const stSeatNoteSchema = z.object({ seatId, text: z.string().max(5_000) });
export const stDismissHelpSchema = z.object({ seatId });
export const stAcceptHelpSchema = z.object({ seatId });
export const playerNightPickSchema = z.object({
    entryId: z.string().min(1).max(128),
    /** Seat ids or character ids, in the order the character's picks[] asks for them. */
    picks: z.array(z.string().min(1).max(64)).max(4),
});
export const playerNominateSchema = z.object({ nomineeSeatId: seatId });
export const playerVoteSchema = z.object({
    nominationId: z.string().min(1).max(64),
    raised: z.boolean(),
});
export const playerNotepadSchema = z.object({ text: z.string().max(20_000) });
export const playerGuessSchema = z.object({
    seatId,
    characterId: characterId.nullable(),
});
export const playerNeighborMessageSchema = z.object({
    chatId: z.string().min(1).max(160),
    text: z.string().trim().min(1).max(500),
});
export const playerRequestHelpSchema = z.object({});
/** Client → server. Keys are the socket event names. */
export const clientEvents = {
    "game:create": gameCreateSchema,
    "game:join": gameJoinSchema,
    "game:leave": gameLeaveSchema,
    "game:resign": gameResignSchema,
    "game:end": gameEndSchema,
    "st:importScript": stImportScriptSchema,
    "st:randomize": stRandomizeSchema,
    "st:assignRole": stAssignRoleSchema,
    "st:revealRoles": stRevealRolesSchema,
    "st:setSeatStatus": stSetSeatStatusSchema,
    "st:moveSeat": stMoveSeatSchema,
    "st:randomizeSeating": stRandomizeSeatingSchema,
    "st:removeSeat": stRemoveSeatSchema,
    "st:startNight": stStartNightSchema,
    "st:endNight": stEndNightSchema,
    "st:nightEntry": stNightEntrySchema,
    "st:nightCursor": stNightCursorSchema,
    "st:startDay": stStartDaySchema,
    "st:startCountdown": stStartCountdownSchema,
    "st:clearCountdown": stClearCountdownSchema,
    "st:unmuteAll": stUnmuteAllSchema,
    "st:openNominations": stOpenNominationsSchema,
    "st:startVote": stStartVoteSchema,
    "st:closeNomination": stCloseNominationSchema,
    "st:clearNominations": stClearNominationsSchema,
    "st:execute": stExecuteSchema,
    "st:exile": stExileSchema,
    "st:endGame": stEndGameSchema,
    "st:viewNotepad": stViewNotepadSchema,
    "st:seatNote": stSeatNoteSchema,
    "st:dismissHelp": stDismissHelpSchema,
    "st:acceptHelp": stAcceptHelpSchema,
    "player:nightPick": playerNightPickSchema,
    "player:nominate": playerNominateSchema,
    "player:vote": playerVoteSchema,
    "player:notepad": playerNotepadSchema,
    "player:guess": playerGuessSchema,
    "player:neighborMessage": playerNeighborMessageSchema,
    "player:requestHelp": playerRequestHelpSchema,
};
//# sourceMappingURL=index.js.map